import {getCopilotSpacePath} from '@github-ui/copilot-chat-helpers/custom-copilots-helpers'
import type {CopilotSpaceBaseRole, CopilotSpaceId, IndexCopilotSpace} from '@github-ui/copilot-chat-types'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {useFetchCopilotSpace, useFetchVisibilitySettings} from '../hooks'
import {useUpsertCopilotSpace} from '../hooks/use-upsert-copilot-space'
import {copyText} from '@github-ui/copy-to-clipboard'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {GlobeIcon, type IconProps, InfoIcon, LinkIcon, LockIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Banner, Button, Dialog, Link, Stack, Truncate} from '@primer/react'
import {clsx} from 'clsx'
import {useEffect, useMemo, useRef, useState} from 'react'

import {SpaceCollaborators} from './SpaceCollaborators'
import styles from './SpaceSharingDialog.module.css'

interface CopilotSpaceSharingDialogProps {
  userOwned?: boolean
  closeDialog: () => void
  customCopilotId: CopilotSpaceId
  returnFocusRef?: React.RefObject<HTMLElement | null> | null
}

interface VisibilitySettingsProps {
  isSpaceLoading: boolean
  copilotSpace?: IndexCopilotSpace
  onError: (errorMessages: Record<string, string>) => void
}

interface BaseRoleSelectProps {
  copilotSpace: IndexCopilotSpace
  onError: (errorMessages: Record<string, string>) => void
}

export function SpaceSharingDialog(props: CopilotSpaceSharingDialogProps) {
  const {closeDialog, customCopilotId, userOwned = false, returnFocusRef} = props
  const [errorMessages, setErrorMessages] = useState<Record<string, string | undefined>>({})
  const [copied, setCopied] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const {isPending: isSpaceLoading, data: currentSpacePayload} = useFetchCopilotSpace(customCopilotId)
  const copilotSpace = currentSpacePayload

  const individualSharingRestricted =
    copilotFeatureFlags.spacesIndividualPoliciesEnabled && !isSpaceLoading && !copilotSpace?.shareable

  const renderFooter = () => {
    if (individualSharingRestricted) {
      return null
    }

    return (
      <CopilotSpaceSharingDialogFooter
        copySharedLink={copySharedLink}
        copied={copied}
        isSpaceLoading={isSpaceLoading}
        copilotSpace={copilotSpace}
      />
    )
  }

  // Clean up timeout when component unmounts
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [])

  const sharedLink = useMemo(
    () => `${ssrSafeLocation.origin}${getCopilotSpacePath(props.customCopilotId)}`,
    [props.customCopilotId],
  )

  const copySharedLink = () => {
    void copyText(sharedLink)
    setCopied(true)
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(() => {
      setCopied(false)
      timeoutRef.current = null
    }, 2000)
  }

  return (
    <Dialog
      position={{narrow: 'fullscreen'}}
      title={'Share this space'}
      renderFooter={renderFooter}
      onClose={() => {
        closeDialog()
        setTimeout(() => returnFocusRef?.current?.focus(), 0)
      }}
      className={styles.spaceVisibilityDialog}
      width="large"
      aria-label="Share this space"
    >
      {individualSharingRestricted && (
        <Banner
          hideTitle
          variant="warning"
          title="individual sharing disabled"
          description={
            <>
              <p>
                Individual space sharing is disabled within your organization. To learn more, check your{' '}
                <Link href={`${ssrSafeLocation.origin}/settings/copilot/features#copilot-spaces-policy`} inline>
                  Copilot Spaces policy settings
                </Link>
                .
              </p>
              <p>In order to share this space, you must duplicate it and change ownership to an organization.</p>
            </>
          }
        />
      )}

      {errorMessages.base || errorMessages.visibility ? (
        <Banner
          hideTitle
          title="Unable to update space"
          description={errorMessages.visibility || errorMessages.base}
          aria-label="Critical"
          variant="critical"
          className={styles.errorBanner}
        />
      ) : null}

      {!individualSharingRestricted && (
        <Stack data-testid="custom-copilot-visibility" gap="condensed">
          {copilotSpace && <SpaceCollaborators copilotSpace={copilotSpace} onError={setErrorMessages} />}
          <ActionList.Divider />
          {userOwned ? (
            <UserOwnedVisibilitySettings
              copilotSpace={copilotSpace}
              isSpaceLoading={isSpaceLoading}
              onError={setErrorMessages}
            />
          ) : (
            <Stack direction="vertical" gap="condensed" align="start">
              <span className="text-small text-bold color-fg-muted">Settings</span>
              <VisibilitySettings
                onError={setErrorMessages}
                copilotSpace={copilotSpace}
                isSpaceLoading={isSpaceLoading}
                {...props}
              />
            </Stack>
          )}
        </Stack>
      )}
    </Dialog>
  )
}

function VisibilitySettings({copilotSpace, isSpaceLoading, onError}: VisibilitySettingsProps) {
  const {
    data: visibilityData,
    isLoading: isVisibilityLoading,
    error: isVisibilityError,
  } = useFetchVisibilitySettings(copilotSpace?.owner || '', copilotSpace?.id ?? -1)

  useEffect(() => {
    if (!isSpaceLoading && !isVisibilityLoading && (!copilotSpace || !visibilityData)) {
      onError({visibility: "Could not load your space's visibility settings"})
    }
  }, [isSpaceLoading, isVisibilityLoading, copilotSpace, visibilityData, onError])

  if (isSpaceLoading) {
    return <div className="color-fg-muted p-2">Loading space information...</div>
  } else if (!copilotSpace) {
    return <div className="color-fg-muted p-2">Could not load data for your space.</div>
  }

  const orgLogin = copilotSpace.owner
  const memberCount = visibilityData?.memberCount

  return (
    <div className="width-full d-flex flex-items-center flex-justify-between">
      <div className="d-flex flex-items-center mt-1">
        <GitHubAvatar src={copilotSpace.ownerAvatar} className="mr-2" square size={32} />
        <div className="d-flex flex-column">
          <span className="ml-1">
            <Truncate maxWidth="230px" title={copilotSpace.ownerDisplayName} inline>
              <b>{copilotSpace.ownerDisplayName}</b>
            </Truncate>
            <span className="color-fg-muted ml-1">(owner)</span>
          </span>
          {orgLogin && (
            <>
              {!isVisibilityLoading && memberCount !== null && (
                <span className="text-small color-fg-muted ml-1">
                  {`${memberCount} ${memberCount === 1 ? 'person' : 'people'}`}
                </span>
              )}
              {(isVisibilityLoading || isVisibilityError || memberCount === null) && (
                <span className="text-small color-fg-muted ml-1">Organization</span>
              )}
            </>
          )}
        </div>
      </div>

      <BaseRoleSelect onError={onError} copilotSpace={copilotSpace} />
    </div>
  )
}

const BaseRoleOptions: Array<{text: string; value: CopilotSpaceBaseRole}> = [
  {text: 'Admin', value: 'custom_copilot_admin'},
  {text: 'Editor', value: 'custom_copilot_writer'},
  {text: 'Viewer', value: 'custom_copilot_reader'},
  {text: 'No access', value: 'none'},
]

const BaseRoleCaptions: Record<CopilotSpaceBaseRole, string> = {
  custom_copilot_admin: 'Can view, make changes and add new people',

  custom_copilot_writer: 'Can view and make changes',

  custom_copilot_reader: 'Can view',
  none: 'Hidden from your organization',
}

function BaseRoleSelect({copilotSpace, onError}: BaseRoleSelectProps) {
  const [open, setOpen] = useState(false)

  const buttonRef = useRef(null)
  const currentBaseRole = BaseRoleOptions.find(opt => opt.value === copilotSpace.baseRole)

  const {upsertCopilotSpace, isPending} = useUpsertCopilotSpace(copilotSpace)

  async function updateSpaceBaseRole(value: CopilotSpaceBaseRole) {
    if (isPending) return
    try {
      await upsertCopilotSpace({baseRole: value})
    } catch {
      onError({base: "An error occurred while updating your space's base role."})
    } finally {
      setOpen(false)
    }
  }

  const baseRoleAriaLabel = currentBaseRole
    ? `Settings ${currentBaseRole.text} ${BaseRoleCaptions[currentBaseRole.value]}`
    : 'Settings'

  return copilotSpace.adminable ? (
    <ActionMenu onOpenChange={setOpen} anchorRef={buttonRef} open={open}>
      <ActionMenu.Button
        loading={isPending}
        variant="invisible"
        className="text-small"
        ref={buttonRef}
        onClick={() => setOpen(true)}
        aria-label={baseRoleAriaLabel}
      >
        <Truncate title={currentBaseRole?.text || ''} inline>
          {currentBaseRole?.text}
        </Truncate>
      </ActionMenu.Button>
      <ActionMenu.Overlay side="outside-bottom" align="end" width="medium">
        <ActionList selectionVariant="single">
          {BaseRoleOptions.map(option => (
            <ActionList.Item
              selected={option.value === currentBaseRole?.value}
              key={option.value}
              onSelect={() => updateSpaceBaseRole(option.value)}
            >
              <span className="text-medium">
                <b>{option.text}</b>
              </span>
              <span className="text-small color-fg-muted">
                <p className="m-0">{BaseRoleCaptions[option.value]}</p>
              </span>
            </ActionList.Item>
          ))}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  ) : (
    <div>
      <Truncate title={currentBaseRole?.text || ''} className="text-small text-light">
        {currentBaseRole?.text}
      </Truncate>
    </div>
  )
}

interface CopilotSpaceSharingDialogFooterProps {
  copySharedLink: () => void
  copied: boolean
  isSpaceLoading: boolean
  copilotSpace?: IndexCopilotSpace
}

function CopilotSpaceSharingDialogFooter({
  copySharedLink,
  copied,
  isSpaceLoading,
  copilotSpace,
}: CopilotSpaceSharingDialogFooterProps) {
  return (
    <Dialog.Footer
      style={{
        borderBottomLeftRadius: 'var(--borderRadius-large, 0.75rem)', // otherwise lost when background-color is set here
        borderBottomRightRadius: 'var(--borderRadius-large, 0.75rem)', // otherwise lost when background-color is set here
        borderTop: '1px solid var(--borderColor-muted)',
        padding: 'var(--base-size-12)',
      }}
      className="color-fg-muted flex-items-center flex-justify-between text-small"
    >
      {!copilotSpace?.ownerIsOrg && (
        <div className={styles.footerText}>
          <InfoIcon />
          <span>Viewers will only see sources they have access to.</span>
        </div>
      )}
      <div style={{minWidth: '90px'}} className="d-flex flex-items-center gap-2">
        {/* Live region to announce link is copied to screen readers */}
        <div aria-live="polite" aria-atomic="true" className="sr-only">
          {copied ? 'Link copied' : ''}
        </div>
        {!copied ? (
          <Button onClick={copySharedLink} leadingVisual={LinkIcon} disabled={isSpaceLoading || !copilotSpace}>
            Copy link
          </Button>
        ) : (
          <Button className="color-fg-success" disabled={isSpaceLoading || !copilotSpace}>
            Link copied
          </Button>
        )}
      </div>
    </Dialog.Footer>
  )
}

// Visibility options for user-owned spaces
type VisibilityOption = {
  text: string
  value: CopilotSpaceBaseRole
  description: string
  icon: React.ComponentType<IconProps>
  color: 'attention' | 'accent'
}

const restrictedOption: VisibilityOption = {
  text: 'Restricted',
  value: 'none',
  description: 'Only people with access can open the link',
  icon: LockIcon,
  color: 'attention',
}

const anyoneWithLinkOption: VisibilityOption = {
  text: 'Anyone with link',
  value: 'custom_copilot_reader',
  description: 'Everyone with access to the resources can access the link',
  icon: GlobeIcon,
  color: 'accent',
}

function getUserOwnedVisibilityOptions(copilotSpace?: IndexCopilotSpace): VisibilityOption[] {
  const isEMU = copilotSpace?.isEnterpriseManaged ?? false

  return isEMU ? [restrictedOption] : [restrictedOption, anyoneWithLinkOption]
}

function UserOwnedVisibilitySettings({copilotSpace, isSpaceLoading, onError}: VisibilitySettingsProps) {
  const [open, setOpen] = useState(false)
  const buttonRef = useRef(null)
  const userOwnedVisibilityOptions = getUserOwnedVisibilityOptions(copilotSpace)

  const currentVisibility: VisibilityOption =
    userOwnedVisibilityOptions.find(opt => opt.value === copilotSpace?.baseRole) ?? restrictedOption
  const {upsertCopilotSpace, isPending} = useUpsertCopilotSpace(copilotSpace)

  async function updateSpaceVisibility(value: CopilotSpaceBaseRole) {
    if (isPending) return
    try {
      await upsertCopilotSpace({baseRole: value})
    } catch {
      onError({base: "An error occurred while updating your space's visibility."})
    } finally {
      setOpen(false)
    }
  }

  const SelectedIcon = currentVisibility.icon

  const visibilityAriaLabel = `General access grouping ${currentVisibility.text} ${currentVisibility.description}`

  if (isSpaceLoading) {
    return <div className="color-fg-muted p-2">Loading space information...</div>
  } else if (!copilotSpace) {
    return <div className="color-fg-muted p-2">Could not load data for your space.</div>
  }

  return (
    <Stack direction="vertical" gap="condensed" align="start">
      <span className="text-small text-bold color-fg-muted">General access</span>
      <div className="width-full d-flex flex-items-center flex-justify-between">
        <div className={styles.visibilityChoice}>
          <div
            className={clsx(
              styles.visibilityIcon,
              currentVisibility.color === 'accent' ? styles.accentIcon : styles.attentionIcon,
            )}
          >
            <SelectedIcon size={16} />
          </div>
          <div className="d-flex flex-column">
            {userOwnedVisibilityOptions.length > 1 ? (
              <ActionMenu onOpenChange={setOpen} anchorRef={buttonRef} open={open}>
                <ActionMenu.Button
                  loading={isPending}
                  variant="invisible"
                  className={styles.visibilityDropdown}
                  ref={buttonRef}
                  onClick={() => setOpen(true)}
                  disabled={!copilotSpace.adminable}
                  aria-label={visibilityAriaLabel}
                >
                  {currentVisibility.text}
                </ActionMenu.Button>
                <ActionMenu.Overlay side="outside-top" align="start" width="small">
                  <ActionList selectionVariant="single">
                    {userOwnedVisibilityOptions.map(option => {
                      const OptionIcon = option.icon
                      return (
                        <ActionList.Item
                          selected={option.value === copilotSpace.baseRole}
                          key={option.value}
                          onSelect={() => updateSpaceVisibility(option.value)}
                        >
                          <ActionList.LeadingVisual>
                            <OptionIcon />
                          </ActionList.LeadingVisual>
                          <div className="d-flex flex-column">
                            <span className="text-medium">{option.text}</span>
                          </div>
                        </ActionList.Item>
                      )
                    })}
                  </ActionList>
                </ActionMenu.Overlay>
              </ActionMenu>
            ) : (
              <span className={styles.visibilityDropdown}>{currentVisibility.text}</span>
            )}
            <span className="text-small color-fg-muted p-1">{currentVisibility.description}</span>
          </div>
        </div>
      </div>
    </Stack>
  )
}
